import {memo, useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {selectIsHost} from '~/common/fabric/User/reducers/user';
import {isDateTimeInPast} from '~/common/utils/time';
import {
  selectEventEnd,
  selectEventId,
  selectEventStart,
} from '~/ViewInvitation/selectors/eventDetails';
import {Button} from '~library/atoms/Button';
import {Link} from '~library/atoms/Link';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {Dropdown} from '~library/molecules/Dropdown';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {SettingsLineIcon} from '~library/svg/SettingsLineIcon';
import {ContactsIcon} from '~library/svg/v2/ContactsIcon';
import {ConversationIcon} from '~library/svg/v2/ConversationIcon';
import {PlusIcon} from '~library/svg/v2/PlusIcon';
import {ShareIcon} from '~library/svg/v2/ShareIcon';

import * as styles from './SettingsButton.module.scss';

interface MenuProps {
  onClose?: () => void;
}

const Menu = memo(({onClose}: MenuProps) => {
  const history = useHistory();
  const isLargishAndUp = useIsLargishAndUp();
  const eventId = useSelector(selectEventId);
  const start = useSelector(selectEventStart);
  const end = useSelector(selectEventEnd);
  const isEventPast = isDateTimeInPast(end || start);

  const sendMessage = useCallback(() => {
    history.push({
      pathname: `/messages/new/`,
      search: history.location.search,
    });
  }, [history]);

  // COMING SOON:
  // const rsvpSettings = useCallback(() => {
  //   history.push({
  //     pathname: '/guests/rsvp-settings',
  //     search: history.location.search,
  //   });
  // }, [history]);

  return (
    <div className={styles.settingsMenu}>
      {isLargishAndUp || (
        <div className={styles.header}>
          <Typography variant="label2" color="pebble">
            Guest List Options
          </Typography>
          <Button variant="transparent" data-qa-id="close-bottom-sheet" onClick={onClose}>
            <CloseLineIcon ratio={0.75} />
          </Button>
        </div>
      )}
      {isEventPast || (
        <Link
          className="linkButton"
          data-qa-id="add-guest-menu-item"
          href={`/invitation/${eventId}/guests`}
          as="transparent"
        >
          <span className={styles.menuText}>
            <PlusIcon ratio={0.75} /> Add guest
          </span>
        </Link>
      )}
      <Button
        className="linkButton"
        variant="transparent"
        data-qa-id="send-message-menu-item"
        size="large"
        onClick={sendMessage}
      >
        <span className={styles.menuText}>
          <ConversationIcon ratio={0.75} /> Send message to guests
        </span>
      </Button>
      <Link
        className="linkButton"
        data-qa-id="export-guestlist-menu-item"
        href={`/event/${eventId}/export/guestlist/csv`}
        as="transparent"
      >
        <span className={styles.menuText}>
          <ShareIcon ratio={0.75} /> Export guest list
        </span>
      </Link>
      {isEventPast || (
        <Link
          className="linkButton"
          data-qa-id="rsvp-settings-menu-item"
          href={`/invitation/${eventId}/guest-options`}
          as="transparent"
        >
          <span className={styles.menuText}>
            <ContactsIcon ratio={0.75} /> RSVP Settings
          </span>
        </Link>
      )}
    </div>
  );
});

Menu.displayName = 'Menu';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sheet = memo(({isOpen, onClose}: SheetProps) => {
  return (
    <BottomSheet
      in={isOpen}
      onClose={onClose}
      content={<Menu onClose={onClose} />}
      data-qa-id="guestlist-settings-sheet"
    />
  );
});

Sheet.displayName = 'Sheet';

export const SettingsButton = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const isLargishAndUp = useIsLargishAndUp();
  const isHost = useSelector(selectIsHost);

  if (!isHost) {
    return null;
  }

  return (
    <>
      <Dropdown
        open={isOpen}
        setOpen={setIsOpen}
        buttonVariant="transparent"
        data-qa-value="guestlist-settings-button"
        buttonQaIdModifier="guestlist-setting"
        trigger={<SettingsLineIcon ratio={0.65} />}
        content={isLargishAndUp && <Menu />}
        offsetLeft={false}
      />
      {!isLargishAndUp && <Sheet isOpen={isOpen} onClose={() => setIsOpen(false)} />}
    </>
  );
});

SettingsButton.displayName = 'SettingsButton';
